import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { api } from '../api';

export default function Profile() {
  const { user, changePassword, deleteAccount, logout, updateStocks } = useAuth();
  const navigate = useNavigate();

  // Change password
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwMsg, setPwMsg] = useState('');
  const [pwError, setPwError] = useState('');
  const [pwLoading, setPwLoading] = useState(false);

  // Delete account
  const [deletePw, setDeletePw] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  // Stock management
  const [newStock, setNewStock] = useState('');
  const [stockError, setStockError] = useState('');
  const [stockSaving, setStockSaving] = useState(false);
  const [allowedSymbols, setAllowedSymbols] = useState<string[]>([]);

  useEffect(() => {
    api.getSymbols()
      .then((symbols) => setAllowedSymbols(Array.isArray(symbols) ? symbols : []))
      .catch(() => setAllowedSymbols([]));
  }, []);

  const handleAddStock = async () => {
    const sym = newStock.trim().toUpperCase();
    if (!sym || !user) return;
    if (!allowedSymbols.includes(sym)) {
      setStockError(`${sym} is not in the admin-approved list`);
      return;
    }
    if (user.stocks?.includes(sym)) { setStockError(`${sym} is already in your list`); return; }
    setStockError('');
    setStockSaving(true);
    try {
      await updateStocks([...(user.stocks || []), sym]);
      setNewStock('');
    } catch (err: any) {
      setStockError(err.message || 'Failed to add stock');
    } finally {
      setStockSaving(false);
    }
  };

  const handleRemoveStock = async (sym: string) => {
    if (!user) return;
    setStockError('');
    setStockSaving(true);
    try {
      await updateStocks((user.stocks || []).filter(s => s !== sym));
    } catch (err: any) {
      setStockError(err.message || 'Failed to remove stock');
    } finally {
      setStockSaving(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwMsg(''); setPwError('');
    if (newPw !== confirmPw) { setPwError('New passwords do not match'); return; }
    setPwLoading(true);
    try {
      await changePassword(currentPw, newPw);
      setPwMsg('Password updated successfully');
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (err: any) {
      setPwError(err.message || 'Failed to change password');
    } finally {
      setPwLoading(false);
    }
  };

  const handleDelete = async (e: React.FormEvent) => {
    e.preventDefault();
    setDeleteError('');
    setDeleteLoading(true);
    try {
      await deleteAccount(deletePw);
      navigate('/login');
    } catch (err: any) {
      setDeleteError(err.message || 'Failed to delete account');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (!user) return null;

  const displayName = user.fullName?.trim() || user.email || 'User';

  const initials = displayName
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const roleBadge: Record<string, string> = {
    admin: 'Admin',
    'user-full': 'Full Access',
    'user-reader': 'Reader',
  };

  return (
    <div style={{ maxWidth: 600 }}>
      <h2>Profile</h2>

      {/* User info card */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
          <div className="profile-avatar-lg">{initials}</div>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{displayName}</div>
            <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>{user.email}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <span className={`badge ${user.role === 'admin' ? 'buy' : 'neutral'}`}>
            {roleBadge[user.role] || user.role}
          </span>
          {user.createdAt && (
            <span style={{ fontSize: 12, color: 'var(--text-muted)', alignSelf: 'center' }}>
              Member since {new Date(user.createdAt).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>

      {/* Manage Stocks */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h3 style={{ marginBottom: 12 }}>My Stocks</h3>
        {stockError && <div className="auth-error">{stockError}</div>}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          {(user.stocks || []).length === 0 && (
            <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>No stocks selected</span>
          )}
          {(user.stocks || []).map(sym => (
            <div key={sym} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'var(--bg)', padding: '6px 12px', borderRadius: 8,
              border: '1px solid var(--border)',
            }}>
              <span style={{ fontWeight: 600, fontSize: 14 }}>{sym}</span>
              <button
                onClick={() => handleRemoveStock(sym)}
                disabled={stockSaving}
                style={{
                  background: 'none', border: 'none', color: 'var(--red)',
                  cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 2px',
                }}
                title={`Remove ${sym}`}
              >×</button>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={newStock}
            onChange={e => setNewStock(e.target.value)}
            disabled={stockSaving || allowedSymbols.length === 0}
            style={{
              padding: '8px 12px', borderRadius: 8,
              border: '1px solid var(--border)', background: 'var(--bg)',
              color: 'var(--text)', fontSize: 14, minWidth: 160,
            }}
          >
            <option value="">— Select a stock —</option>
            {allowedSymbols
              .filter(s => !(user.stocks || []).includes(s))
              .map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
          </select>
          <button
            onClick={handleAddStock}
            disabled={stockSaving || !newStock.trim()}
            className="auth-btn"
            style={{
              width: 'auto', padding: '8px 20px',
              opacity: (stockSaving || !newStock.trim()) ? 0.5 : 1,
            }}
          >
            {stockSaving ? 'Saving…' : 'Add Stock'}
          </button>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            Only stocks approved by admin can be added.
          </span>
        </div>
      </div>

      {/* Change password */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h3 style={{ marginBottom: 16 }}>Change Password</h3>
        <form onSubmit={handleChangePassword}>
          {pwMsg && <div className="auth-success">{pwMsg}</div>}
          {pwError && <div className="auth-error">{pwError}</div>}
          <div className="auth-field">
            <label>Current Password</label>
            <input type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)} required minLength={8} />
          </div>
          <div className="auth-field">
            <label>New Password</label>
            <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} required minLength={8} />
          </div>
          <div className="auth-field">
            <label>Confirm New Password</label>
            <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} required minLength={8} />
          </div>
          <button type="submit" className="auth-btn" disabled={pwLoading} style={{ width: 'auto' }}>
            {pwLoading ? 'Updating…' : 'Update Password'}
          </button>
        </form>
      </div>

      {/* Danger zone */}
      <div className="card" style={{ borderColor: 'var(--red)' }}>
        <h3 style={{ color: 'var(--red)', marginBottom: 12 }}>Danger Zone</h3>
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            className="auth-btn"
            style={{ width: 'auto', background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)' }}
            onClick={() => { logout(); navigate('/login'); }}
          >
            Sign Out
          </button>
          <button
            className="auth-btn"
            style={{ width: 'auto', background: 'var(--red)' }}
            onClick={() => setShowDelete(!showDelete)}
          >
            Delete Account
          </button>
        </div>

        {showDelete && (
          <form onSubmit={handleDelete} style={{ marginTop: 16 }}>
            {deleteError && <div className="auth-error">{deleteError}</div>}
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
              This action is irreversible. Enter your password to confirm.
            </p>
            <div className="auth-field">
              <input
                type="password"
                value={deletePw}
                onChange={e => setDeletePw(e.target.value)}
                placeholder="Enter your password"
                required
              />
            </div>
            <button type="submit" className="auth-btn" style={{ width: 'auto', background: 'var(--red)' }} disabled={deleteLoading}>
              {deleteLoading ? 'Deleting…' : 'Permanently Delete Account'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
